// Rule-based home hero (Step A of the personalised hero plan).
// Uses only what the browser already knows: campaign tags, referrer, pages seen on
// this site and local time. No CRM data, no personal data. Search engines and the
// prerender snapshot always get the default banner.

const PAGES_KEY = 'acolead_pages_seen';
const CAMPAIGN_KEY = 'acolead_first_touch';
const VISITOR_KEY = 'acolead_hero_visitor';
const HOLDOUT_SHARE = 0.15;

const storage = (kind) => {
  try {
    const s = window[kind];
    const probe = '__acolead_probe__';
    s.setItem(probe, '1');
    s.removeItem(probe);
    return s;
  } catch {
    return null;
  }
};

const readJson = (store, key, fallback) => {
  if (!store) return fallback;
  try {
    const value = JSON.parse(store.getItem(key) || 'null');
    return value ?? fallback;
  } catch {
    return fallback;
  }
};

const writeJson = (store, key, value) => {
  if (!store) return;
  try {
    store.setItem(key, JSON.stringify(value));
  } catch {
    // Storage full or blocked: personalisation simply falls back to the default.
  }
};

const isSnapshot = () => typeof navigator === 'undefined' || /ReactSnap|bot|crawl|spider|Lighthouse/i.test(navigator.userAgent || '');

// Called on every route change from the router.
export const recordPageView = (pathname) => {
  if (typeof window === 'undefined' || isSnapshot()) return;
  const local = storage('localStorage');
  const seen = readJson(local, PAGES_KEY, {});
  const path = (pathname || '/').replace(/\/+$/, '') || '/';
  seen[path] = (seen[path] || 0) + 1;
  const entries = Object.entries(seen).sort((a, b) => b[1] - a[1]).slice(0, 60);
  writeJson(local, PAGES_KEY, Object.fromEntries(entries));
};

const firstTouch = () => {
  const session = storage('sessionStorage');
  const params = new URLSearchParams(window.location.search);
  const fromUrl = {
    source: (params.get('utm_source') || '').toLowerCase(),
    medium: (params.get('utm_medium') || '').toLowerCase(),
    campaign: (params.get('utm_campaign') || '').toLowerCase(),
    content: (params.get('utm_content') || '').toLowerCase(),
    gclid: params.has('gclid') || params.has('gbraid') || params.has('wbraid'),
    fbclid: params.has('fbclid'),
    referrer: (() => {
      try {
        const ref = document.referrer ? new URL(document.referrer) : null;
        return ref && ref.host !== window.location.host ? ref.host.toLowerCase() : '';
      } catch {
        return '';
      }
    })(),
  };
  const hasSignal = fromUrl.source || fromUrl.campaign || fromUrl.gclid || fromUrl.fbclid || fromUrl.referrer;
  if (hasSignal) {
    writeJson(session, CAMPAIGN_KEY, fromUrl);
    return fromUrl;
  }
  return readJson(session, CAMPAIGN_KEY, fromUrl);
};

const visitorBucket = () => {
  const local = storage('localStorage');
  let bucket = readJson(local, VISITOR_KEY, null);
  if (typeof bucket !== 'number') {
    bucket = Math.random();
    writeJson(local, VISITOR_KEY, bucket);
  }
  return bucket;
};

const has = (text, words) => words.some((w) => text.includes(w));

const AI_HOSTS = ['chatgpt.com', 'chat.openai.com', 'claude.ai', 'perplexity.ai', 'gemini.google.com', 'copilot.microsoft.com'];
const PORTAL_HOSTS = ['99acres', 'magicbricks', 'housing.com', 'nobroker', 'squareyards', 'bayut', 'propertyfinder', 'dubizzle', 'rightmove', 'zoopla', 'idealista', 'immobilienscout24', 'zillow', 'realtor.com'];

export const resolveHero = () => {
  if (typeof window === 'undefined' || isSnapshot()) return { id: 'default', reason: 'snapshot', holdout: false };

  const forced = new URLSearchParams(window.location.search).get('hero');
  if (forced && HERO_VARIANTS[forced]) return { id: forced, reason: 'override', holdout: false };

  const t = firstTouch();
  const campaignText = `${t.campaign} ${t.content}`;
  const seen = readJson(storage('localStorage'), PAGES_KEY, {});
  const featurePages = Object.keys(seen).filter((p) => p.startsWith('/features/') || p.startsWith('/ai/'));
  const hour = new Date().getHours();
  const day = new Date().getDay();

  let match = null;
  if (has(campaignText, ['mandate'])) match = ['mandate', 'campaign'];
  else if (has(campaignText, ['channel', '_cp', 'cp_', 'broker-partner'])) match = ['partners', 'campaign'];
  else if (has(campaignText, ['resale', 'broker'])) match = ['resale', 'campaign'];
  else if (has(campaignText, ['builder', 'developer'])) match = ['builders', 'campaign'];
  else if (has(campaignText, ['zlb', 'leak', 'zero-lead', 'zero_lead'])) match = ['zlb', 'campaign'];
  else if (t.fbclid || has(t.source, ['facebook', 'instagram', 'meta', 'fb', 'ig'])) match = ['meta', 'campaign'];
  else if (t.gclid || (t.source.includes('google') && has(t.medium, ['cpc', 'paid', 'ppc']))) match = ['google', 'campaign'];
  else if (AI_HOSTS.some((h) => t.referrer.includes(h))) match = ['ai', 'referrer'];
  else if (PORTAL_HOSTS.some((h) => t.referrer.includes(h))) match = ['portal', 'referrer'];
  else if ((seen['/zero-lead-breach'] || 0) >= 2) match = ['zlb', 'pages'];
  else if ((seen['/pricing'] || 0) >= 1) match = ['pricing', 'pages'];
  else if (featurePages.length >= 3) match = ['researching', 'pages'];
  else if (hour < 9 || hour >= 20 || day === 0) match = ['afterHours', 'time'];

  if (!match) return { id: 'default', reason: 'none', holdout: false };
  if (visitorBucket() < HOLDOUT_SHARE) return { id: 'default', reason: match[1], holdout: true, wouldBe: match[0] };
  return { id: match[0], reason: match[1], holdout: false };
};

// Copy for each banner. `cta` is either 'demo' (scroll to the form) or a path.
export const HERO_VARIANTS = {
  default: {
    eyebrow: 'Real estate CRM software',
    title: ['The Real Estate CRM That ', 'Never Lets a Lead Go Cold'],
    sub: 'Capture leads from portals, Meta, Instagram, WhatsApp and walk-ins, route each one to the right salesperson in seconds, and let the AI Sales Agent cover every enquiry your team can’t reach in time.',
    primary: ['Book a Free Demo', 'demo'],
    secondary: ['See All Features', 'features'],
  },
  meta: {
    eyebrow: 'For teams running Meta ads',
    title: ['Every Meta Lead ', 'Called Within Minutes'],
    sub: 'Facebook and Instagram lead forms arrive in AcoLead instantly, go to the right salesperson, and the AI Sales Agent follows up if nobody calls in time. See cost per visit and booking for every campaign.',
    primary: ['Book a Free Demo', 'demo'],
    secondary: ['See Lead Capture', '/features/lead-capture'],
  },
  google: {
    eyebrow: 'For teams running Google Ads',
    title: ['Know Which Ad ', 'Brought Each Booking'],
    sub: 'Connect ad spend to leads, completed site visits and bookings, source by source. See where leads leak and which campaigns deserve more budget.',
    primary: ['Book a Free Demo', 'demo'],
    secondary: ['See Marketing ROI', '/features/marketing-roi'],
  },
  portal: {
    eyebrow: 'Portal leads in real time',
    title: ['Every Portal Enquiry, ', 'Answered in Minutes'],
    sub: 'Leads from 99acres, Housing.com, MagicBricks and other marketplaces land in AcoLead the moment they arrive, tagged with the project and routed to the right salesperson.',
    primary: ['Book a Free Demo', 'demo'],
    secondary: ['See Lead Capture', '/features/lead-capture'],
  },
  zlb: {
    eyebrow: 'Zero Lead Breach',
    title: ['Stop Leads ', 'Leaving With Your Staff'],
    sub: 'Your team keeps normal SIM calls and WhatsApp on company phones, while customer numbers stay protected and every call and chat is captured for review.',
    primary: ['Book a Free Demo', 'demo'],
    secondary: ['See Zero Lead Breach', '/zero-lead-breach'],
  },
  afterHours: {
    eyebrow: 'AI Sales Agent',
    title: ['Leads Arrive at Night. ', 'Who Answers Them?'],
    sub: 'When your team is offline, the AcoLead AI Sales Agent replies on WhatsApp, calls to qualify the buyer and books the site visit, then hands the lead to its owner in the morning.',
    primary: ['Book a Free Demo', 'demo'],
    secondary: ['See the AI Sales Agent', '/ai/sales-agent'],
  },
  ai: {
    eyebrow: 'AI Connector',
    title: ['Ask Your Real Estate CRM ', 'in ChatGPT or Claude'],
    sub: 'Connect AcoLead to your AI assistant and ask about leads, follow-ups, site visits and team numbers in plain language, with answers limited to what your role can see.',
    primary: ['Book a Free Demo', 'demo'],
    secondary: ['See AI Connector', '/ai/connector'],
  },
  pricing: {
    eyebrow: 'Plans for every team size',
    title: ['A Real Estate CRM Plan ', 'That Fits Your Team'],
    sub: 'Tell us your team size and the modules you need, and we’ll show AcoLead on your own leads and prepare a quote in one call.',
    primary: ['Get Your Quote', 'demo'],
    secondary: ['See Pricing', '/pricing'],
  },
  researching: {
    eyebrow: 'Seen enough features?',
    title: ['See AcoLead on ', 'Your Own Projects'],
    sub: 'In 20 minutes we’ll set up your projects, lead sources and team, and show your next leads flowing through AcoLead.',
    primary: ['Book a 20-min Demo', 'demo'],
    secondary: ['See Customers', '/customers'],
  },
  builders: {
    eyebrow: 'For builders and developers',
    title: ['One CRM for Projects, ', 'Site Visits and Payments'],
    sub: 'Inventory, site visits, channel partners and post-sales collections in one real estate CRM, with every lead routed and followed up.',
    primary: ['Book a Free Demo', 'demo'],
    secondary: ['See Builder Setup', '/solutions/builders'],
  },
  partners: {
    eyebrow: 'For channel partner agencies',
    title: ['Close More Deals ', 'Across Builder Projects'],
    sub: 'Capture every enquiry, follow up on WhatsApp, track site visits and bill builders for brokerage from one CRM.',
    primary: ['Book a Free Demo', 'demo'],
    secondary: ['See Channel Partner Setup', '/solutions/channel-partners'],
  },
  mandate: {
    eyebrow: 'For mandate companies',
    title: ['Run Every Mandate and ', 'Your CP Network'],
    sub: 'Separate projects and builders, prove marketing results with cost per visit and booking, and give every CP its own AcoLead CRM.',
    primary: ['Book a Free Demo', 'demo'],
    secondary: ['See Mandate Setup', '/solutions/mandate-companies'],
  },
  resale: {
    eyebrow: 'For brokers and resale firms',
    title: ['List, Match and ', 'Close Resale Deals'],
    sub: 'Owner listings, approvals and buyer matching in one resale flow, published to your own website and shared on WhatsApp.',
    primary: ['Book a Free Demo', 'demo'],
    secondary: ['See Resale Flow', '/solutions/resale'],
  },
};
